import {gsap} from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import CustomEase from "gsap/CustomEase";
import {getBootstrapContainerMargins, removePaddingScrollbar, setPaddingScrollbar, startPageOnTop} from "../utilities.js";
import {matchMediaMDUp, matchMediaXlUp} from "../mediaQuery.js";
import Swiper from 'swiper';

gsap.registerPlugin(ScrollTrigger, CustomEase);

export default function (lenis) {
    startPageOnTop();

    /**
     * Animation logo hero
     */
    (() => {
        const section = '.f-hero';
        const logo = '.f-hero__logo img';
        const overlay = '.f-hero__overlay';
        const heroInner = '.f-hero__inner';
        const heroContent = '.f-hero__content';

        setPaddingScrollbar()
        lenis.stop()

        const startScroll = () => {
            removePaddingScrollbar()
            lenis.start()
        }

        let tl = gsap.timeline({
            onComplete: startScroll
        });

        tl
            .to(logo, {
                scale: 1,
                opacity: 1,
                maskPosition: 'center center, center center',
                duration: 3,
                delay: 2
            })
            .fromTo(heroContent, {
                y: -100,
                opacity: 0,
            }, {
                y: 0,
                opacity: 1,
                duration: 2
            })
            .to(logo, {
                y: matchMediaMDUp.matches ? '40%' : '10%',
                scale: 0.9,
                duration: 2
            }, '<').to(overlay, {
            opacity: 1,
            duration: 2
        }, '<')

        let tl2 = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'start start',
                end: '+=100%',
                scrub: true,
                markers: false,
            }
        });

        tl2.to(heroInner, {
            marginLeft: getBootstrapContainerMargins().left || 12,
            marginRight: getBootstrapContainerMargins().right || 12,
        }).to(logo, {
            y: '70%'
        }, '<')
    })();

    /**
     * Video Animation
     */
    (() => {
        const videoSection = '.section-animated-video__video';
        const video = '.video-target';
        const logoSection = '.section-animated-video__logo';
        const logo = '.logo-target';
        const textSection = '.section-animated-video__text';

        const videoTl = gsap.timeline({
            scrollTrigger: {
                trigger: videoSection,
                start: 'bottom bottom',
                end: '+=1500',
                scrub: true,
                markers: false,
            }
        })

        const logoTl = gsap.timeline({
            scrollTrigger: {
                trigger: logoSection,
                start: 'bottom bottom',
                end: '+=1000',
                pin: false,
                scrub: true,
                markers: false,
            }
        })

        const textTl = gsap.timeline({
            scrollTrigger: {
                trigger: textSection,
                start: 'start 80%',
                scrub: true,
                markers: false
            }
        })

        videoTl.to(videoSection, {
            y: 0,
            ease: "power1.in",
        }).to(video, {
            aspectRatio: 'auto',
            width: '100%',
            maxWidth: '100%',
            height: '100%',
        }).to(video, {
            clipPath: "inset(0 0 100% 0)",
        })

        logoTl.to(logo, {
            opacity: 1,
            scale: 0.5,
        }).to(logo, {
            top: '120px'
        })

        textTl.fromTo(textSection, {
            y: '30%'
        }, {
            y: 0
        }).to(textSection, {
            opacity: 0,
        })

        // gsap.to('.test-sci', {
        //     y: 0,
        //     scrollTrigger: {
        //         trigger: '.test-text',
        //         start: 'top top',
        //         pin: true,
        //         scrub: true,
        //         markers: true
        //     }
        // })
    })();

    /**
     * Expandable Image
     */
    (() => {
        const section = '.gallery-grid-section';
        const image = '.f-expandable-image__image';

        gsap.to(image, {
            width: '100%',
            duration: 5,
            ease: "power3.out",
            scrollTrigger: {
                trigger: section,
                start: 'start 60%',
                once: true,
                markers: false,
            }
        })
    })();

    /**
     * Gallery grid
     */
    (() => {
        const section = '.gallery-grid-section';
        const grid = '.f-gallery-grid';

        gsap.to(grid, {
            css: {
                '--grid-gap': matchMediaXlUp.matches ? 30 : 25,
                '--translate-x': matchMediaXlUp.matches ? '-10%' : '-5%',
            },
            scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                scrub: 2,
                markers: false,
            }
        })
    })();

    /**
     * Animated slider
     */
    (() => {
        const getSnap = () => {
            return matchMediaMDUp.matches ? {
                snapTo: [0, 1],
                ease: "power3.out",
            } : false
        }

        const swiperEl = document.querySelectorAll('.f-slider__slider');

        const swiperParams = {
            watchSlidesProgress: true,
            slidesPerView: matchMediaMDUp.matches ? 2.5 : 2,
            on: {
                progress(swiper) {
                    swiper.slides.forEach((slide) => {
                        const inner = slide.querySelector('.slide-inner');
                        if (!inner) return;

                        const p = slide.progress;
                        const clamped = Math.min(Math.abs(p), 1);

                        const scale = 1 - clamped * 0.4;
                        const opacity = 1 - clamped * 0.6;

                        inner.style.transform = `scale(${scale})`;
                        inner.style.opacity = opacity;
                    });
                },
                setTransition(swiper, duration) {
                    swiper.slides.forEach((slide) => {
                        const inner = slide.querySelector('.slide-inner');
                        if (!inner) return;
                        inner.style.transitionDuration = `${duration}ms`;
                    });
                },
            },
        };

        swiperEl.forEach(swiper => {
            Object.assign(swiper, swiperParams);
            swiper.initialize();
        })

        // Content animation
        // const section = ".f-section-slider";
        // gsap.to(section, {
        //     css: {
        //         '--y': '-100%',
        //     },
        //     scrollTrigger: {
        //         trigger: section,
        //         start: 'start start',
        //         pin: true,
        //         scrub: true,
        //         snap: getSnap(),
        //         markers: false,
        //     }
        // })
    })();

    /**
     * Change swiper
     */
    (() => {
        const tabs = document.querySelectorAll('.f-slider__tabs [data-target]');
        const swipers = document.querySelectorAll('.f-slider__slider');
        const paginations = document.querySelectorAll('.f-slider__pagination');

        function showSwiper(key) {
            swipers.forEach(sw => {
                const isTarget = sw.dataset.swiper === key;
                sw.classList.toggle('d-none', !isTarget);
                sw.classList.toggle('is-active', isTarget);

                if (isTarget && sw.swiper) {
                    sw.swiper.update();
                }
            });

            paginations.forEach(sw => {
                const isTarget = sw.dataset.swiper === key;
                sw.classList.toggle('d-none', !isTarget);
                sw.classList.toggle('is-active', isTarget);
            });
        }

        tabs.forEach(btn => {
            btn.addEventListener('click', () => {
                const key = btn.dataset.target;

                tabs.forEach(b => {
                    const active = b === btn;
                    b.classList.toggle('active', active);
                    b.classList.toggle('btn-outline', !active);
                });

                showSwiper(key);
            });
        });

        showSwiper('bindings');
    })();
}
